import { Expose, Type } from 'class-transformer';
import {
    IsArray,
    IsBoolean,
    IsEnum,
    IsInt,
    IsOptional,
    IsString,
    Max,
    MaxLength,
    Min,
    ValidateNested,
} from 'class-validator';

import {
    AttractionFactDecisionEnum,
    AttractionFactOriginEnum,
    AttractionFactSource,
    AttractionFactSourceDto,
    AttractionFactTruthEnum,
    AttractionFactVerdictEnum,
} from '../city-attraction/attraction-fact';
import { IsNotBlank } from '../shared/is-not-blank';

/**
 * Fact bank of a venue. The pitch is written only from approved facts.
 * Claims are Russian; source quotes stay verbatim. Canon: docs/venue-pitch-guide.md.
 */

/** Which move of the pitch a fact can carry. */
export enum EstablishmentFactBeatEnum {
    /** Contradiction or striking visible detail for the opening line. */
    HOOK = 'hook',
    /** Metaphor or contrast that explains how the place works. */
    WIT = 'wit',
    /** A person and their passion, or a ritual done with the guest. */
    WARMTH = 'warmth',
    /** What to order, where to sit, what time of day. */
    TIP = 'tip',
    /** How to find the door; lands in findHint, not in the pitch. */
    FIND = 'find',
    BACKGROUND = 'background',
}

export type EstablishmentFactCandidateRO = {
    id: number;
    createdAt: string | Date;
    updatedAt: string | Date;
    establishmentId: number;
    origin: AttractionFactOriginEnum;
    claim: string;
    detail: string | null;
    year: string | null;
    beat: EstablishmentFactBeatEnum | null;
    truthStatus: AttractionFactTruthEnum;
    sources: AttractionFactSource[];
    /** Menus, prices, line-ups: true means the pitch needs a recheck date. */
    volatile: boolean;
    /** Last time sources were saved; volatile facts are rechecked from here. */
    checkedAt: string | Date | null;
    aiRating: number | null;
    aiVerdict: AttractionFactVerdictEnum | null;
    aiNotes: string | null;
    aiReviewer: string | null;
    aiReviewedAt: string | Date | null;
    editorDecision: AttractionFactDecisionEnum;
    editorBeat: EstablishmentFactBeatEnum | null;
    editorNote: string | null;
    reviewedByEmail: string | null;
    reviewedAt: string | Date | null;
    /** Where the fact landed, e.g. `ru:pitch`, `ru:findHint`. */
    usedIn: string[];
};

export class CreateEstablishmentFactDto {
    @IsNotBlank()
    @MaxLength(2000)
    @Expose()
    claim: string;

    @IsOptional()
    @IsString()
    @Expose()
    detail?: string | null;

    @IsOptional()
    @IsString()
    @Expose()
    year?: string | null;

    @IsOptional()
    @IsEnum(EstablishmentFactBeatEnum)
    @Expose()
    beat?: EstablishmentFactBeatEnum | null;

    @IsOptional()
    @IsEnum(AttractionFactTruthEnum)
    @Expose()
    truthStatus?: AttractionFactTruthEnum;

    @IsOptional()
    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => AttractionFactSourceDto)
    @Expose()
    sources?: AttractionFactSourceDto[];

    @IsOptional()
    @IsBoolean()
    @Expose()
    volatile?: boolean;
}

/** Editor update: content fixes plus the human decision. */
export class UpdateEstablishmentFactDto {
    @IsOptional()
    @IsNotBlank()
    @MaxLength(2000)
    @Expose()
    claim?: string;

    @IsOptional()
    @IsString()
    @Expose()
    detail?: string | null;

    @IsOptional()
    @IsString()
    @Expose()
    year?: string | null;

    @IsOptional()
    @IsEnum(EstablishmentFactBeatEnum)
    @Expose()
    beat?: EstablishmentFactBeatEnum | null;

    @IsOptional()
    @IsEnum(AttractionFactTruthEnum)
    @Expose()
    truthStatus?: AttractionFactTruthEnum;

    @IsOptional()
    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => AttractionFactSourceDto)
    @Expose()
    sources?: AttractionFactSourceDto[];

    @IsOptional()
    @IsBoolean()
    @Expose()
    volatile?: boolean;

    @IsOptional()
    @IsEnum(AttractionFactDecisionEnum)
    @Expose()
    editorDecision?: AttractionFactDecisionEnum;

    @IsOptional()
    @IsEnum(EstablishmentFactBeatEnum)
    @Expose()
    editorBeat?: EstablishmentFactBeatEnum | null;

    @IsOptional()
    @IsString()
    @Expose()
    editorNote?: string | null;
}

/** Agent review; touches only ai* fields, the truth status and the suggested beat. */
export class ScoreEstablishmentFactDto {
    @IsInt()
    @Expose()
    factId: number;

    @IsOptional()
    @IsEnum(AttractionFactTruthEnum)
    @Expose()
    truthStatus?: AttractionFactTruthEnum;

    @IsOptional()
    @IsEnum(AttractionFactVerdictEnum)
    @Expose()
    aiVerdict?: AttractionFactVerdictEnum;

    @IsOptional()
    @IsInt()
    @Min(0)
    @Max(100)
    @Expose()
    aiRating?: number;

    @IsOptional()
    @IsEnum(EstablishmentFactBeatEnum)
    @Expose()
    beat?: EstablishmentFactBeatEnum | null;

    @IsOptional()
    @IsString()
    @Expose()
    aiNotes?: string | null;
}
